import { useContext, useState } from 'react'
import type { CollectionTreeExpandedService } from 'objects/services/CollectionTreeExpandedService.ts'
import { Box, Collapse } from '@mui/material'
import { ToggleExpandButton } from 'core/app-shell/sidebar/collapsible-menu/ToggleExpandButton.tsx'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import FolderIcon from '@mui/icons-material/Folder'
import { SidebarTypoButton } from 'core/app-shell/sidebar/collapsible-menu/SidebarTypoButton.tsx'
import { explorerNodesConfig } from './ExplorerNodesConfig.tsx'
import clsx from 'clsx'
import { CollectionTreeContext } from './ICollectionTreeContext.tsx'
import type { NodeOptionPopoverItem } from './HierarchyNodeOptionsPopoverItemsProps.tsx'
import type { NewNodePopoverResult } from './NewNodePopoverItems.tsx'
import { useDiRef } from '@/common/react/di-hooks'
import type { CollectionTreeController } from './CollectionsTreeController'
import type { CollectionsTreeService } from '../services/CollectionsTreeService'
import { RenamableNodeName } from './HierarchyNodeName.tsx'
import { PopoverOptionsMenuIcon } from '@/core/popover/options/PopoverOptionsMenuIcon.tsx'
import type { FolderModel } from '@/core/gql/gql-models-override.ts'

export interface FolderNodeProps {
  subtreeVersion: number
  folder: FolderModel
  level: number
  children?: React.ReactNode
}

const config = explorerNodesConfig

export function FolderNode({ folder, level, children }: FolderNodeProps) {
  const { optionPopoverHooks, newNodePopoverHooks } = useContext(CollectionTreeContext)!
  const expandedStateService = useDiRef<CollectionTreeExpandedService>(
    'CollectionTreeExpandedService',
  )
  const [isOpen, setIsOpen] = useState(() => expandedStateService.isExpanded(folder.id))
  const [isHeaderHoveredOn, setIsHeaderHoveredOn] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const controller = useDiRef<CollectionTreeController>('CollectionTreeController')
  const collectionsService = useDiRef<CollectionsTreeService>('CollectionsTreeService')

  function onPopoverOptionsSelected(option: NodeOptionPopoverItem) {
    switch (option) {
      case 'Rename': {
        setIsRenaming(true)
        break
      }
      case 'Delete': {
        void collectionsService.deleteFolder(folder.id)
        break
      }
    }
  }

  function onNewNodePopoverOptionSelected(result: NewNodePopoverResult) {
    controller.onNewNodePopoverItemClicked(result, folder.id)
    setIsOpen(true)
    expandedStateService.setExpanded(folder.id, true)
  }

  function onRenameSubmit(name: string) {
    void collectionsService.updateFolder({ id: folder.id, name })
  }

  return (
    <Box
      sx={{
        marginLeft: level == 0 ? 0 : config.levelIndent,
        display: 'flex',
        justifyContent: 'flex-start',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          ...config.nodeSx,
          pl: 0,
          justifyContent: 'space-between',
        }}
        className={clsx('sidebarNewTypoButtonContainer', 'sidebarMenuListItem')}
        onClick={() => {
          const next = !isOpen
          setIsOpen(next)
          expandedStateService.setExpanded(folder.id, next)
        }}
        onPointerEnter={() => setIsHeaderHoveredOn(true)}
        onPointerLeave={() => setIsHeaderHoveredOn(false)}
      >
        <Box className="folderHeaderGroupingContainer">
          {isHeaderHoveredOn ? (
            <ToggleExpandButton
              sx={{
                width: `${config.iconSize}px`,
                height: `${config.iconSize}px`,
              }}
              open={isOpen}
            />
          ) : (
            <Box display="flex">
              {isOpen ? <FolderOpenIcon sx={config.iconSx} /> : <FolderIcon sx={config.iconSx} />}
            </Box>
          )}
          <RenamableNodeName
            {...{ isRenaming, setIsRenaming, onRenameSubmit, initialName: folder.name }}
          />
        </Box>

        <Box className="folderHeaderGroupingContainer" sx={{ justifyContent: 'flex-end' }}>
          <PopoverOptionsMenuIcon
            onClick={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation()
              optionPopoverHooks.openPopover(e.currentTarget, onPopoverOptionsSelected)
            }}
          />

          <Box
            sx={{
              display: 'flex',
              alignSelf: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              width: config.nodeHeightPx,
              height: config.nodeHeightPx,
              '&:hover': {
                backgroundColor: '#d1d5db',
              },
            }}
          >
            <SidebarTypoButton
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.stopPropagation()
                newNodePopoverHooks.openPopover(e.currentTarget, onNewNodePopoverOptionSelected)
              }}
              text="+"
              sx={{ ml: 0, fontSize: 25 }}
            />
          </Box>
        </Box>
      </Box>

      <Collapse in={isOpen}>
        <Box
          sx={{
            ml: `4px`,
            borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box className="folderChildren" sx={{ pl: 1.5 }}>
            {children}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}
